/**
 * Evaluates small JSON predicates against flat borrower answer objects.
 * Used when loan_program_eligibility.match_predicate is non-null (future).
 *
 * Operand shapes:
 *   { eq: [answerKey, value] }
 *   { neq: [answerKey, value] }
 *   { in: [answerKey, [v1, v2]] }
 *   { not_in: [answerKey, [v1, v2]] }
 *   { gte: [answerKey, number] } — numeric compare when answer parses as number
 *   { lte: [answerKey, number] }
 *   { between: [answerKey, min, max] }
 *   { truthy: [answerKey] }
 *   { falsy: [answerKey] }
 *   { all: [predicate, ...] }
 *   { any: [predicate, ...] }
 *   { none: [predicate, ...] }
 */

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function getAnswer(answers, key) {
  if (!answers || key == null) return undefined;
  return answers[key];
}

export function evaluatePredicate(predicate, answers = {}) {
  if (predicate == null) return true;
  if (typeof predicate !== 'object') return false;

  if ('eq' in predicate) {
    const [k, want] = predicate.eq;
    return String(getAnswer(answers, k) ?? '') === String(want);
  }
  if ('neq' in predicate) {
    const [k, want] = predicate.neq;
    return String(getAnswer(answers, k) ?? '') !== String(want);
  }
  if ('in' in predicate) {
    const [k, arr] = predicate.in;
    const val = getAnswer(answers, k);
    return Array.isArray(arr) && arr.some((x) => String(x) === String(val));
  }
  if ('not_in' in predicate) {
    const [k, arr] = predicate.not_in;
    const val = getAnswer(answers, k);
    return Array.isArray(arr) && !arr.some((x) => String(x) === String(val));
  }
  if ('gte' in predicate) {
    const [k, bound] = predicate.gte;
    const v = num(getAnswer(answers, k));
    const b = num(bound);
    return v != null && b != null && v >= b;
  }
  if ('lte' in predicate) {
    const [k, bound] = predicate.lte;
    const v = num(getAnswer(answers, k));
    const b = num(bound);
    return v != null && b != null && v <= b;
  }
  if ('between' in predicate) {
    const [k, lo, hi] = predicate.between;
    const v = num(getAnswer(answers, k));
    const a = num(lo);
    const b = num(hi);
    return v != null && a != null && b != null && v >= Math.min(a, b) && v <= Math.max(a, b);
  }
  if ('truthy' in predicate) {
    const [k] = predicate.truthy;
    const v = getAnswer(answers, k);
    return v === true || v === 'true' || v === 'yes' || v === 'Yes' || (typeof v === 'string' && v.length > 0 && v !== 'no' && v !== 'No');
  }
  if ('falsy' in predicate) {
    const [k] = predicate.falsy;
    const v = getAnswer(answers, k);
    return !v || v === 'no' || v === 'No' || v === 'false' || v === false;
  }
  if ('all' in predicate) {
    const arr = predicate.all;
    return Array.isArray(arr) && arr.every((p) => evaluatePredicate(p, answers));
  }
  if ('any' in predicate) {
    const arr = predicate.any;
    return Array.isArray(arr) && arr.some((p) => evaluatePredicate(p, answers));
  }
  if ('none' in predicate) {
    const arr = predicate.none;
    return Array.isArray(arr) && !arr.some((p) => evaluatePredicate(p, answers));
  }

  return false;
}

export default { evaluatePredicate };
